<template>
  <div style="min-height: 100vh" class="email-client fill-height d-flex flex-column grey darken-4">
    <v-app-bar v-if="$vuetify.breakpoint.smAndDown" app dense flat color="grey darken-4" dark>
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
      <v-toolbar-title class="white--text">{{ activeMail && showMobileDetail ? "E-Mail" : formatFolderName(selectedFolder) }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="syncMails" :loading="syncing"><v-icon color="grey lighten-1">mdi-refresh</v-icon></v-btn>
    </v-app-bar>

    <div class="d-flex flex-grow-1" style="height: 100%; overflow: hidden; position: relative">
      <v-navigation-drawer
        v-model="drawer"
        :permanent="$vuetify.breakpoint.mdAndUp"
        :absolute="$vuetify.breakpoint.smAndDown"
        width="250"
        color="#121212"
        dark>
        <v-toolbar flat dense color="transparent" v-if="$vuetify.breakpoint.mdAndUp">
          <v-toolbar-title class="font-weight-bold white--text">Postfach</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon small @click="syncMails" :loading="syncing">
            <v-icon color="grey lighten-1">mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <v-list dense nav class="mt-2" dark>
          <v-list-item-group v-model="selectedFolder" active-class="primary--text text--lighten-2">
            <v-list-item
              v-for="folder in folders"
              :key="folder.name"
              :value="folder.name"
              @click="loadMails(folder.name)"
              @dragover.prevent
              @drop="onDrop($event, folder.name)">
              <v-list-item-icon>
                <v-icon>{{ getFolderIcon(folder.name) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="grey--text text--lighten-2">{{ formatFolderName(folder.name) }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-navigation-drawer>

      <v-card
        width="380"
        class="d-flex flex-column rounded-0 elevation-0"
        color="#1E1E1E"
        style="border-right: 1px solid #333"
        v-show="$vuetify.breakpoint.mdAndUp || !showMobileDetail">
        <v-toolbar flat dense color="transparent" class="px-2 pt-2">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            label="Suchen..."
            hide-details
            single-line
            dense
            outlined
            rounded
            dark
            color="white"
            background-color="grey darken-3"
            class="search-input"></v-text-field>
        </v-toolbar>

        <v-list two-line class="overflow-y-auto flex-grow-1" color="#1E1E1E" dark>
          <template v-if="loadingMails">
            <v-skeleton-loader dark v-for="n in 6" :key="n" type="list-item-two-line" class="mx-2 my-1 transparent"></v-skeleton-loader>
          </template>

          <template v-else-if="mails.length === 0">
            <div class="pa-5 text-center grey--text">
              <v-icon size="48" color="grey darken-1">mdi-inbox-outline</v-icon>
              <p class="mt-2">Keine E-Mails gefunden.</p>
            </div>
          </template>

          <v-list-item-group v-else v-model="selectedMailIndex" active-class="active-mail-item">
            <v-list-item
              v-for="(mail, index) in mails"
              :key="mail._id"
              @click="selectMail(mail)"
              draggable="true"
              @dragstart="onDragStart($event, mail)"
              :class="getMailRowClass(mail)"
              style="border-bottom: 1px solid rgba(255, 255, 255, 0.05)">
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold d-flex justify-space-between mb-1">
                  <span class="text-truncate white--text" style="max-width: 70%">{{ mail.from.name || mail.from.address }}</span>
                  <span class="caption grey--text">{{ formatDate(mail.date) }}</span>
                </v-list-item-title>

                <v-list-item-subtitle class="grey--text text--lighten-1 mb-1 font-weight-medium">
                  {{ mail.subject }}
                </v-list-item-subtitle>

                <v-list-item-subtitle>
                  <v-chip
                    x-small
                    label
                    v-if="mail.category && mail.category !== 'General'"
                    :color="getCategoryColor(mail.category)"
                    class="mr-1 mb-1"
                    >{{ mail.category }}</v-chip
                  >
                  <span class="caption grey--text text--darken-1">{{ mail.aiAnalysis ? mail.aiAnalysis.summary : "Lade..." }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-card>

      <v-card
        class="flex-grow-1 d-flex flex-column rounded-0 elevation-0"
        color="grey darken-4"
        v-if="!($vuetify.breakpoint.smAndDown && !showMobileDetail)">
        <v-card-text v-if="!activeMail" class="d-flex align-center justify-center fill-height">
          <div class="text-center grey--text text--darken-1">
            <v-icon size="80" color="grey darken-3">mdi-email-open-outline</v-icon>
            <h3 class="mt-4 font-weight-light">Wähle eine Nachricht aus</h3>
          </div>
        </v-card-text>

        <template v-else>
          <v-toolbar dense flat color="grey darken-4" style="border-bottom: 1px solid rgba(255, 255, 255, 0.05)">
            <v-btn icon v-if="$vuetify.breakpoint.smAndDown" @click="showMobileDetail = false" dark>
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="archiveMail(activeMail)" dark
                  ><v-icon color="grey lighten-1">mdi-archive-outline</v-icon></v-btn
                >
              </template>
              <span>Archivieren</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="deleteMail(activeMail)" dark
                  ><v-icon color="grey lighten-1">mdi-delete-outline</v-icon></v-btn
                >
              </template>
              <span>Löschen</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="openForward" dark><v-icon color="grey lighten-1">mdi-share-variant</v-icon></v-btn>
              </template>
              <span>Weiterleiten</span>
            </v-tooltip>

            <v-spacer></v-spacer>

            <v-btn small text color="white accent-2" @click="showMagicDialog = true"> <v-icon left small>mdi-auto-fix</v-icon> AI & TAC </v-btn>

            <v-btn v-if="activeMail.customerData" color="success" small depressed class="ml-2" @click="showCustomerInfo = true">
              <v-icon left small>mdi-card-account-details</v-icon> Member
            </v-btn>
            <v-chip v-else small label color="grey darken-2" class="ml-2 white--text" outlined>Kein Member</v-chip>
          </v-toolbar>

          <div class="pa-4 grey darken-4">
            <div class="d-flex justify-space-between align-start">
              <h2 class="title font-weight-bold mb-2 white--text">{{ activeMail.subject }}</h2>
              <div class="caption grey--text">{{ new Date(activeMail.date).toLocaleString() }}</div>
            </div>

            <div class="d-flex align-center mt-2">
              <v-avatar color="primary" size="36" class="white--text mr-3">
                {{ (activeMail.from.name || activeMail.from.address)[0].toUpperCase() }}
              </v-avatar>
              <div>
                <div class="subtitle-2 white--text">
                  {{ activeMail.from.name }}
                  <span class="caption grey--text">&lt;{{ activeMail.from.address }}&gt;</span>
                </div>
                <div class="caption grey--text">An: {{ activeMail.to.map((t) => t.address).join(", ") }}</div>
              </div>
            </div>

            <v-alert
              dense
              border="left"
              colored-border
              color="white accent-4"
              elevation="0"
              color-text="white"
              class="mt-4 mb-0 grey darken-3"
              icon="mdi-robot">
              <div class="d-flex align-center justify-space-between white--text">
                <div>
                  <strong class="white--text text--accent-2">Roody Analyse:</strong>
                  {{ activeMail.aiAnalysis ? activeMail.aiAnalysis.summary : "Keine Analyse." }}
                </div>
              </div>
            </v-alert>
          </div>

          <v-divider class="grey darken-3"></v-divider>

          <div class="flex-grow-1 overflow-y-auto pa-4 grey darken-4">
            <div class="mail-renderer pa-2 elevation-2">
              <div v-html="activeMail.body"></div>
            </div>

            <v-expand-transition>
              <div v-if="activeMail.draft && !replyMode && !isForwarding" class="mt-6 mb-2">
                <v-card outlined color="grey darken-3" class="rounded-lg elevation-4" style="border: 1px solid rgba(124, 77, 255, 0.4)">
                  <v-card-title class="subtitle-2 font-weight-bold white--text text--accent-2 py-2">
                    <v-icon small color="white accent-2" class="mr-2">mdi-robot-outline</v-icon>
                    ROODY AI ENTWURF
                  </v-card-title>

                  <v-divider class="white darken-3" style="opacity: 0.3"></v-divider>

                  <v-card-text class="white--text body-1 pt-4 pb-2" style="white-space: pre-wrap">
                    <div v-html="activeMail.draft"></div>
                  </v-card-text>

                  <v-card-actions class="px-4 pb-3">
                    <v-btn small outlined color="white accent-1" @click="showMagicDialog = true" class="mr-2">
                      <v-icon left small>mdi-refresh</v-icon>
                      Neu Generieren / TAC
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-btn small text color="grey lighten-1" @click="initReply">
                      Diesen Text übernehmen
                      <v-icon right small>mdi-arrow-down</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </v-expand-transition>
          </div>

          <div class="pa-4 grey darken-4" style="border-top: 1px solid rgba(255, 255, 255, 0.05)">
            <v-expand-transition>
              <div v-if="replyMode">
                <div class="d-flex align-center mb-2" v-if="isForwarding">
                  <span class="caption mr-2 grey--text">An:</span>
                  <v-text-field
                    v-model="forwardTo"
                    dense
                    outlined
                    hide-details
                    dark
                    placeholder="empfaenger@beispiel.ch"
                    class="flex-grow-1"></v-text-field>
                </div>

                <v-textarea
                  v-model="replyText"
                  outlined
                  dark
                  :label="isForwarding ? 'Nachricht' : 'Antwort'"
                  rows="6"
                  auto-grow
                  background-color="grey darken-3"></v-textarea>

                <div class="d-flex justify-end">
                  <v-btn text @click="replyMode = false" class="mr-2 white--text">Abbrechen</v-btn>
                  <v-btn color="primary" @click="sendReply" :loading="sending"> <v-icon left>mdi-send</v-icon> Senden </v-btn>
                </div>
              </div>
              <div v-else class="d-flex">
                <v-btn color="primary" depressed @click="initReply"> <v-icon left>mdi-reply</v-icon> Antworten (AI Draft) </v-btn>
                <v-btn text class="ml-2 white--text" @click="openForward"> <v-icon left>mdi-share</v-icon> Weiterleiten </v-btn>
              </div>
            </v-expand-transition>
          </div>
        </template>
      </v-card>

      <v-dialog v-model="showCustomerInfo" max-width="600">
        <v-card v-if="tacMember" color="grey darken-3" dark>
          <v-card-title class="headline grey darken-4">
            Mitglieder-Check
            <v-spacer></v-spacer>
            <v-btn icon @click="showCustomerInfo = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="d-flex align-center mb-4">
              <v-avatar size="64" color="grey darken-2">
                <span class="headline white--text">{{ tacMember.initials }}</span>
              </v-avatar>
              <div class="ml-4">
                <h2 class="title white--text">{{ tacMember.fullName }}</h2>
                <div class="subtitle-2 grey--text">Nr: {{ tacMember.memberNumber }}</div>
              </div>
              <v-spacer></v-spacer>
              <div class="text-right">
                <v-chip :color="tacMember.contractState === 1 ? 'green darken-2' : 'red darken-2'" dark small class="mb-1">
                  {{ tacMember.contractState === 1 ? "AKTIV" : "INAKTIV" }}
                </v-chip>
                <div class="caption grey--text">Bis: {{ tacMember.endDate }}</div>
              </div>
            </div>

            <v-divider class="mb-3 grey darken-2"></v-divider>

            <v-row>
              <v-col cols="6">
                <div class="caption grey--text">Guthaben</div>
                <div
                  class="title"
                  :class="{ 'red--text text--lighten-1': tacMember.balance < 0, 'green--text text--lighten-1': tacMember.balance >= 0 }">
                  CHF {{ tacMember.balance }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="caption grey--text">Tarif</div>
                <div class="body-1 white--text">{{ tacMember.categoryName }}</div>
              </v-col>
            </v-row>

            <v-list dense class="transparent" dark>
              <v-list-item class="px-0">
                <v-list-item-icon><v-icon color="grey">mdi-email</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-title>{{ tacMember.email }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
              <v-list-item class="px-0">
                <v-list-item-icon><v-icon color="grey">mdi-phone</v-icon></v-list-item-icon>
                <v-list-item-content
                  ><v-list-item-title>{{ tacMember.phone }}</v-list-item-title></v-list-item-content
                >
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showMagicDialog" max-width="500">
        <v-card color="grey darken-3" dark>
          <v-card-title class="headline">AI & Member Management</v-card-title>
          <v-card-text class="pt-2">
            <p class="body-2 grey--text text--lighten-1">
              Hier kannst du den Absender manuell einem TAC-Profil zuordnen und den AI-Draft mit neuen Instruktionen neu schreiben lassen.
            </p>

            <v-text-field
              v-model="manualEmailInput"
              label="TAC E-Mail suchen"
              append-icon="mdi-magnify"
              @click:append="lookupTacUser"
              outlined
              dense
              dark
              class="mt-3"></v-text-field>

            <div v-if="foundTacUser" class="mb-4 pa-2 rounded" style="border: 1px solid #4caf50; background: rgba(76, 175, 80, 0.1)">
              <div class="d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                <div>
                  <div class="font-weight-bold white--text">
                    {{ foundTacUser.MemberDetails.MemberDetail[0].Profile.Profile.Profile.Customer.PersonName.FirstName }}
                    {{ foundTacUser.MemberDetails.MemberDetail[0].Profile.Profile.Profile.Customer.PersonName.LastName }}
                  </div>
                  <div class="caption grey--text">Gefunden. Wird beim Klick auf "Generieren" verknüpft.</div>
                </div>
              </div>
            </div>

            <v-textarea
              v-model="aiInstructions"
              label="Instruktionen für die AI"
              placeholder="z.B. 'Sei etwas formeller', 'Erwähne das offene Saldo'"
              outlined
              rows="3"
              dark></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="showMagicDialog = false" class="white--text">Abbrechen</v-btn>
            <v-btn @click="regenerateAi" :loading="regenerating"> <v-icon left>mdi-refresh</v-icon> Generieren </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import moment from "moment"

export default {
  name: "EmailClient",
  data: () => ({
    drawer: true,
    folders: [{ name: "INBOX" }, { name: "Sent" }, { name: "Trash" }],
    selectedFolder: "INBOX",
    mails: [],
    loadingMails: false,
    selectedMailIndex: null,
    activeMail: null,
    search: "",
    syncing: false,
    showMobileDetail: false,

    // Popup Info
    showCustomerInfo: false,

    // Popup Magic/AI
    showMagicDialog: false,
    manualEmailInput: "",
    foundTacUser: null,
    aiInstructions: "",
    regenerating: false,

    // Reply / Forward
    replyMode: false,
    isForwarding: false,
    replyText: "",
    forwardTo: "",
    sending: false,
  }),
  computed: {
    tacMember() {
      const sourceData = this.activeMail?.customerData
      if (!sourceData) return null

      try {
        const details = sourceData.MemberDetails?.MemberDetail || []
        if (details.length === 0) return null

        const root = details.sort((a, b) => {
          const aIsActive = a.ContractState === 1 ? 1 : 0
          const bIsActive = b.ContractState === 1 ? 1 : 0
          if (aIsActive !== bIsActive) return bIsActive - aIsActive
          return new Date(b.UsageEndDate) - new Date(a.UsageEndDate)
        })[0]

        const profile = root?.Profile?.Profile?.Profile?.Customer
        const address = root?.Profile?.Profile?.Profile?.Addresses?.NameAddress?.[0]

        let balance = 0
        if (root.Accounts && root.Accounts.Account) {
          root.Accounts.Account.forEach((acc) => {
            balance += parseFloat(acc.Balance.value)
          })
        }

        let phone = "n/a"
        if (root?.Profile?.Profile?.Profile?.Phones?.NamePhone) {
          const p =
            root.Profile.Profile.Profile.Phones.NamePhone.find((x) => x.phoneType === "MOBILE") || root.Profile.Profile.Profile.Phones.NamePhone[0]
          if (p) phone = p.PhoneData.PhoneNumber
        }

        return {
          memberNumber: root.MemberNumber,
          contractState: root.ContractState,
          endDate: moment(root.ContractEndDate).format("DD.MM.YYYY"),
          balance: balance.toFixed(2),
          categoryName: root.MemberCategoryName,
          fullName: `${profile?.PersonName?.FirstName} ${profile?.PersonName?.LastName}`,
          initials: `${profile?.PersonName?.FirstName?.[0]}${profile?.PersonName?.LastName?.[0]}`,
          email: profile?.AccountName,
          phone: phone,
          address: address?.AddressLine1 || "",
          zipCity: `${address?.PostalCode || ""} ${address?.CityName || ""}`,
        }
      } catch (e) {
        console.error("TAC Parse Error", e)
        return null
      }
    },
  },
  async mounted() {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.drawer = false
    }
    await this.fetchFolders()
    await this.loadMails("INBOX")
  },
  methods: {
    formatDate(date) {
      const m = moment(date)
      if (m.isSame(moment(), "day")) return m.format("HH:mm")
      return m.format("DD.MM.")
    },
    formatFolderName(name) {
      return name.replace("INBOX.", "")
    },
    getFolderIcon(name) {
      const n = name.toLowerCase()
      if (n.includes("inbox")) return "mdi-inbox"
      if (n.includes("sent")) return "mdi-send"
      if (n.includes("trash") || n.includes("bin")) return "mdi-delete"
      return "mdi-folder"
    },
    getCategoryColor(cat) {
      if (!cat) return "grey darken-2"
      const c = cat.toLowerCase()
      // Farben anpassen für Dark Mode Kontrast
      if (c.includes("cancel") || c.includes("künd")) return "red darken-4 white--text"
      if (c.includes("sales") || c.includes("verkau")) return "green darken-4 white--text"
      if (c.includes("support")) return "blue darken-4 white--text"
      return "grey darken-3 grey--text"
    },
    getMailRowClass(mail) {
      let classes = []
      // Dark Mode Logic Only
      if (!mail.isRead) classes.push("grey darken-3 font-weight-bold white--text")
      else classes.push("transparent grey--text text--lighten-2")
      return classes.join(" ")
    },

    /* --- DRAG & DROP --- */
    onDragStart(evt, mail) {
      evt.dataTransfer.dropEffect = "move"
      evt.dataTransfer.effectAllowed = "move"
      evt.dataTransfer.setData("mailId", mail._id)
    },
    async onDrop(evt, targetFolder) {
      const mailId = evt.dataTransfer.getData("mailId")
      if (!mailId) return

      const mailIndex = this.mails.findIndex((m) => m._id === mailId)
      if (mailIndex > -1) this.mails.splice(mailIndex, 1)

      try {
        await api.mail.update(mailId, { folder: targetFolder })
        this.$toast.success(`In ${targetFolder} verschoben`)
      } catch (e) {
        this.$toast.error("Verschieben fehlgeschlagen")
        await this.loadMails(this.selectedFolder)
      }
    },

    /* --- DATA ACTIONS --- */
    async fetchFolders() {
      try {
        const res = await api.mail.getFolders()
        if (Array.isArray(res)) this.folders = res
      } catch (e) {
        console.error(e)
      }
    },
    async loadMails(folder) {
      this.loadingMails = true
      this.selectedFolder = folder
      this.activeMail = null
      this.replyMode = false
      this.showMobileDetail = false

      try {
        const res = await api.mail.getMails({ folder })
        this.mails = res.mails || []
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingMails = false
      }
    },
    async syncMails() {
      this.syncing = true
      try {
        await api.mail.sync()
        this.$toast.success("Postfach synchronisiert")
        await this.loadMails(this.selectedFolder)
      } catch (e) {
        this.$toast.error("Fehler beim Sync")
      } finally {
        this.syncing = false
      }
    },
    async selectMail(mail) {
      this.activeMail = mail
      this.replyMode = false
      this.showCustomerInfo = false
      this.showMobileDetail = true

      if (!mail.isRead) {
        mail.isRead = true
        api.mail.update(mail._id, { isRead: true })
      }
    },
    async archiveMail(mail) {
      try {
        await api.mail.update(mail._id, { folder: "Archive" })
        this.mails = this.mails.filter((m) => m._id !== mail._id)
        this.activeMail = null
        this.showMobileDetail = false
      } catch (e) {
        this.$toast.error("Fehler beim Archivieren")
      }
    },
    async deleteMail(mail) {
      try {
        await api.mail.update(mail._id, { folder: "Trash" })
        this.mails = this.mails.filter((m) => m._id !== mail._id)
        this.activeMail = null
        this.showMobileDetail = false
      } catch (e) {
        this.$toast.error("Fehler beim Löschen")
      }
    },

    /* --- MAGIC AI / MANUAL TAC --- */
    async lookupTacUser() {
      if (!this.manualEmailInput) return
      try {
        const res = await api.mail.manualTacLookup(this.manualEmailInput)
        if (res && res.MemberDetails) {
          this.foundTacUser = res
          this.$toast.success("Mitglied gefunden!")
        } else {
          this.$toast.info("Kein Mitglied gefunden.")
          this.foundTacUser = null
        }
      } catch (e) {
        this.$toast.error("Fehler bei der Suche")
      }
    },
    // In methods:

    async regenerateAi() {
      if (!this.activeMail) return
      this.regenerating = true

      try {
        // Der Backend Call gibt jetzt das komplette, neue Mail-Objekt zurück
        const updatedMail = await api.mail.regenerate({
          mailId: this.activeMail._id,
          newCustomerEmail: this.foundTacUser ? this.manualEmailInput : null,
          instructions: this.aiInstructions,
        })

        // 1. Das Mail im Array finden und komplett ersetzen (Vue 2 Reactivity Fix)
        const index = this.mails.findIndex((m) => m._id === updatedMail._id)
        if (index !== -1) {
          // this.$set zwingt Vue, das Array-Update zu erkennen
          this.$set(this.mails, index, updatedMail)

          // Active Mail Referenz aktualisieren
          this.activeMail = this.mails[index]
        }

        // 2. UI Reset
        this.showMagicDialog = false
        this.foundTacUser = null
        this.aiInstructions = ""
        this.manualEmailInput = ""

        // 3. LIVE UPDATE: Falls das Antwort-Fenster schon offen ist oder der User den Draft sehen will
        // Wir überschreiben replyText direkt, wenn wir nicht im Forward Mode sind.
        if (this.replyMode && !this.isForwarding) {
          this.replyText = updatedMail.draft
          this.$toast.success("AI Draft in Editor geladen")
        } else {
          this.$toast.success("AI Analyse & Draft aktualisiert")
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Generieren")
      } finally {
        this.regenerating = false
      }
    },

    /* --- REPLY & FORWARD --- */
    initReply() {
      this.replyMode = true
      this.isForwarding = false
      if (this.activeMail.draft) {
        this.replyText = this.activeMail.draft
      } else {
        this.replyText = `Hallo ${this.activeMail.from.name},\n\n`
      }
    },
    openForward() {
      this.replyMode = true
      this.isForwarding = true
      this.forwardTo = ""
      this.replyText = `
---------- Forwarded message ---------
From: ${this.activeMail.from.name} <${this.activeMail.from.address}>
Date: ${new Date(this.activeMail.date).toLocaleString()}
Subject: ${this.activeMail.subject}

${this.activeMail.textBody || "Inhalt nicht verfügbar"}
      `
    },
    async sendReply() {
      this.sending = true
      try {
        const payload = {
          html: this.replyText.replace(/\n/g, "<br>"),
          replyToId: this.activeMail._id,
        }

        if (this.isForwarding) {
          payload.to = this.forwardTo
          payload.subject = "Fwd: " + this.activeMail.subject
        } else {
          payload.to = this.activeMail.from.address
          payload.subject = "Re: " + this.activeMail.subject
        }

        await api.mail.send(payload)
        this.$toast.success("Gesendet")
        this.replyMode = false
      } catch (e) {
        this.$toast.error("Fehler beim Senden")
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
/* Force Dark Scrollbars */
::-webkit-scrollbar {
  width: 8px;
  background-color: #1e1e1e;
}
::-webkit-scrollbar-thumb {
  background-color: #424242;
  border-radius: 4px;
}

.active-mail-item {
  background-color: rgba(187, 134, 252, 0.15) !important; /* Slight Purple tint */
  border-left: 3px solid #bb86fc !important;
}

/* MAIL RENDERER MAGIC 
  Das hier ist der Trick, damit schwarzer Text weiß wird.
*/
.mail-renderer {
  background-color: white; /* Basis ist weiß, wie eine normale Mail */
  color: black;
  /* Invertiere Helligkeit (Schwarz -> Weiß) aber rotiere Hue 180grad (damit Blau Blau bleibt) */
  filter: invert(0.92) hue-rotate(180deg);
  border-radius: 4px;
  overflow: hidden;
}

/* Bilder wieder zurück-drehen, damit sie normal aussehen */
.mail-renderer >>> img {
  filter: invert(1) hue-rotate(180deg);
  max-width: 100%;
  height: auto;
}

/* Links lesbar machen */
.mail-renderer >>> a {
  color: #0000ee; /* Standard Link Blau, wird durch den Filter zu Hellblau/Orange je nach Rotate */
}

/* Zitate grau machen */
.mail-renderer >>> blockquote {
  border-left: 3px solid #ccc;
  padding-left: 10px;
  color: #444;
}
</style>
